import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { useGenieAccess } from "@/hooks/billing/useAccessLimits";
import { getRandomGenieDefaultImage } from "@/data/genie/defaults";
import { supabase } from "@/integrations/supabase/client";
import { AgentService } from "@/services/agent/core";
import { SetupRecoveryService } from "@/services/agent/recovery";
import { SetupFunnelService } from "@/services/analytics/SetupFunnelService";
import { AccountBrandingService } from "@/services/branding/AccountBrandingService";
import { ConversationMessage, SetupAnalysisResult } from "@/types/agent/setup";
import { DEFAULT_BRANDING, GenieBranding } from "@/types/ui/branding";
import { extractColorPalette, suggestTextColors } from "@/utils/image/colorExtraction";

export const useConversationAnalysis = () => {
  const { canCreateGenie, requiresUpgrade } = useGenieAccess();
  const { team } = useAuth();
  const queryClient = useQueryClient();

  // Helper to get account branding if available
  const getAccountBranding = useCallback(async (): Promise<GenieBranding | null> => {
    if (!team?.id) return null;
    try {
      return await AccountBrandingService.getAccountBranding(team.id);
    } catch (err) {
      console.warn("Failed to fetch account branding:", err);
      return null;
    }
  }, [team?.id]);

  const analyzeConversation = useCallback(
    async (localMessages: ConversationMessage[], conversationId?: string | null, userId?: string | null): Promise<SetupAnalysisResult> => {
      console.log("analyzeConversation called with conversation ID:", conversationId, "user ID:", userId);

      // Call edge function for analysis
      const { data, error } = await supabase.functions.invoke("handle-conversation", {
        body: {
          action: "setup",
          data: {
            conversationId: conversationId || undefined,
            messages: localMessages.length > 0 ? localMessages : undefined,
            userId: userId || undefined,
          },
        },
      });

      if (error) {
        console.error("Edge function error:", error);
        throw new Error(error.message || "Failed to analyze conversation");
      }

      if (!data?.success || !data?.analysis) {
        throw new Error(data?.message || "Failed to analyze conversation");
      }

      const analysis = data.analysis;
      console.log("Analysis result from edge function:", analysis);

      // Generate branding for preview if not already provided
      if (!analysis.branding || (!analysis.branding.customAvatarUrl && !analysis.branding.logoUrl)) {
        // First, check if account branding is available
        const accountBranding = await getAccountBranding();

        if (accountBranding) {
          // Use account branding as the default for new Genies
          console.log("Using account branding as default for new Genie");
          analysis.branding = { ...accountBranding };
        } else {
          // No account branding, generate random preview branding
          const defaultImage = getRandomGenieDefaultImage();

          // Create base branding with random image
          let brandingToPreview = {
            ...DEFAULT_BRANDING,
            customAvatarUrl: defaultImage,
            logoUrl: defaultImage,
          };

          // Extract colors from the selected image
          try {
            const { suggestedPrimary, suggestedSecondary } = await extractColorPalette(defaultImage);
            const { primaryText } = suggestTextColors(suggestedPrimary);

            brandingToPreview = {
              ...brandingToPreview,
              primaryColor: suggestedPrimary,
              secondaryColor: suggestedSecondary,
              gradientEnabled: true,
              textColor: primaryText,
              qrCodeStyle: {
                ...(brandingToPreview.qrCodeStyle || DEFAULT_BRANDING.qrCodeStyle!),
                foregroundColor: suggestedPrimary,
              },
              embedStyle: {
                ...(brandingToPreview.embedStyle || DEFAULT_BRANDING.embedStyle!),
                headerColor: suggestedPrimary,
              },
            };

            console.log("Generated preview branding with colors:", { primary: suggestedPrimary, secondary: suggestedSecondary });
          } catch (err) {
            console.warn("Auto-brand color extraction failed; using defaults:", err);
          }

          analysis.branding = brandingToPreview;
        }
      }

      // Write the derived setup down NOW, while we have it. Recovery used to
      // re-derive this from the transcript on demand, which meant a setup
      // became unrecoverable the moment that transcript aged out. Fire and
      // forget: failing to save the safety net must not fail the analysis.
      void SetupRecoveryService.persistDerivedSetupState(conversationId, {
        analysis,
        transcript: localMessages,
      });

      return analysis;
    },
    [getAccountBranding]
  );

  const createGenieFromAnalysis = useCallback(
    async (finalAnalysis: SetupAnalysisResult) => {
      // Check if user has permission to create a genie
      if (!canCreateGenie) {
        const errorMessage = requiresUpgrade
          ? "You've reached your genie limit. Please upgrade your subscription to create more genies."
          : "You don't have permission to create genies.";
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }

      console.log("Launching genie creation");

      // If user selected to create for team owner, set impersonatedUserId
      const analysisToSend = { ...finalAnalysis };
      if (finalAnalysis.createForTeamOwner && team?.owner_id) {
        analysisToSend.impersonatedUserId = team.owner_id;
      }

      const createdAgent = await AgentService.create(analysisToSend);

      SetupFunnelService.completed(finalAnalysis.setupConversationId || null);

      console.log("Agent created successfully:", createdAgent);
      toast.success(`Genie "${finalAnalysis.genieName}" created successfully!`);

      queryClient.invalidateQueries({ queryKey: ["agents-list"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });

      return createdAgent;
    },
    [canCreateGenie, requiresUpgrade]
  );

  return {
    analyzeConversation,
    createGenieFromAnalysis,
  };
};
